import React, { useMemo } from "react";
import { formatMoney } from "../../../../features/utils/formatMoney";
import {
  getChangeAdminGuidance,
  hasChangeQuote,
} from "../../../../data/flight/order/getChangeAdminGuidance";

function RenameConfirmationPriceBreakdown({ data }) {
  const guidance = getChangeAdminGuidance(data);
  const quoted = hasChangeQuote(data);
  const emptyQuote =
    !quoted &&
    ["needs_review", "auto_processing", "needs_quote", "validation_failed"].includes(
      guidance.stage,
    );

  const travellerLib = {
    ADT: "Adult",
    CHD: "Child",
    CNN: "Child",
    INF: "Infant",
    INS: "Infant",
  };

  const passengers = data?.passengerInfo || [];
  const raw = data?.priceBreakdown || [];

  const cachedAdmin = useMemo(() => {
    try {
      return JSON.parse(
        sessionStorage.getItem(`changeQuoteAdmin:${data?._id}`) || "null",
      );
    } catch (_) {
      return null;
    }
  }, [data?._id]);

  const perPassenger = useMemo(() => {
    return raw.map((it) => {
      const p = passengers.find((pp) => pp?.id === it?.passengerId) || {};
      const travellerType =
        it?.travellerType || travellerLib[p?.travelerType] || "Adult";
      const name =
        it?.passenger ||
        [p?.firstName, p?.middleName, p?.lastName].filter(Boolean).join(" ") ||
        travellerType;

      const cacheRow = (cachedAdmin?.priceBreakdown || []).find(
        (c) => String(c?.passengerId) === String(it?.passengerId),
      );

      const linesSubtotal =
        (it.changePrice || 0) +
        (it.ticketDifference || 0) +
        (it.penaltyFee || 0);

      const airlineChange =
        it.airlineChangePrice ??
        cacheRow?.airlineChangePrice ??
        data?.airlineChangePrice ??
        cachedAdmin?.airlineChangePrice ??
        null;
      const airlineFare =
        it.airlineFareDifference ??
        cacheRow?.airlineFareDifference ??
        data?.airlineFareDifference ??
        cachedAdmin?.airlineFareDifference ??
        null;
      const airlinePenalty =
        it.airlinePenaltyFee ??
        cacheRow?.airlinePenaltyFee ??
        data?.airlineChangePenalty ??
        cachedAdmin?.airlineChangePenalty ??
        null;
      const fareMarkup =
        it.fareDifferenceMarkup ??
        cacheRow?.fareDifferenceMarkup ??
        data?.fareDifferenceMarkup ??
        cachedAdmin?.fareDifferenceMarkup ??
        null;

      return {
        ...it,
        travellerType,
        passenger: name,
        linesSubtotal,
        airlineChange,
        airlineFare,
        airlinePenalty,
        fareMarkup,
      };
    });
  }, [raw, passengers, data, cachedAdmin]);

  const serviceFee = Number(
    data?.intraverseServiceFee ||
      data?.changeFee ||
      cachedAdmin?.intraverseServiceFee ||
      0,
  );

  if (emptyQuote) {
    return (
      <div className="w-full border h-fit border-gray-200 rounded-xl overflow-hidden bg-white mt-4 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">
            {guidance.isDetails ? "Details change" : "Change"}
          </h2>
        </div>
        <div className="bg-primary1/5 p-4 text-sm text-gray-700 gap-2 flex flex-col">
          <div className="bg-[#FE9F29]/10 w-full rounded-md min-h-9 px-3 flex items-center justify-center text-[#FE9F29] font-semibold text-sm text-center">
            {guidance.statusLabel}
          </div>
          <span className="text-xs text-gray-600 text-center">
            {guidance.description}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full border h-fit border-gray-200 rounded-xl overflow-hidden bg-white mt-4 p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900">
          {guidance.isDetails ? "Details change" : "Change"}
        </h2>
        <StatusBadge data={data} />
      </div>
      {guidance.stage === "needs_airline_submit" ||
      guidance.stage === "send_failed" ? (
        <p className="text-xs text-gray-600 mb-3">{guidance.description}</p>
      ) : null}
      {guidance.stage === "needs_reissue" ||
      guidance.stage === "settlement_failed" ? (
        <p className="text-xs text-gray-600 mb-3">{guidance.description}</p>
      ) : null}

      <div className="bg-primary1/5 p-4 sm:p-6 text-sm text-gray-700">
        {perPassenger.map((row) => (
          <div key={row._id || row.passengerId} className="mb-4 last:mb-0">
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium">1 × {row.travellerType}</span>
              <span className="font-semibold text-gray-900">
                {formatMoney(row.linesSubtotal)}
              </span>
            </div>

            <div className="pl-4 space-y-1">
              {row.airlineChange != null && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Airline change fee</span>
                  <span className="text-gray-600">
                    {formatMoney(row.airlineChange || 0)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">
                  Change fee (agent pays)
                </span>
                <span className="text-gray-700">
                  {formatMoney(row.changePrice || 0)}
                </span>
              </div>

              {row.airlineFare != null && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Airline fare difference</span>
                  <span className="text-gray-600">
                    {formatMoney(row.airlineFare || 0)}
                  </span>
                </div>
              )}
              {(row.fareMarkup != null && Number(row.fareMarkup) !== 0) && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Markup on fare</span>
                  <span className="text-emerald-700">
                    {formatMoney(row.fareMarkup || 0)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">
                  Fare difference (agent pays)
                </span>
                <span className="text-gray-700">
                  {formatMoney(row.ticketDifference || 0)}
                </span>
              </div>

              {row.airlinePenalty != null && Number(row.airlinePenalty) > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Airline penalty</span>
                  <span className="text-gray-600">
                    {formatMoney(row.airlinePenalty || 0)}
                  </span>
                </div>
              )}
              {(row.penaltyFee || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">
                    Penalty (airline cost, no markup)
                  </span>
                  <span className="text-gray-700">
                    {formatMoney(row.penaltyFee || 0)}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}

        <div className="flex justify-between items-center mt-4 mb-2 rounded-lg bg-[#FFFBEB] border border-[#FEF3C7] px-3 py-2">
          <span className="font-medium text-[#92400E]">
            Intraverse service fee
          </span>
          <span className="font-semibold text-[#92400E]">
            {formatMoney(serviceFee)}
          </span>
        </div>

        <hr className="border-gray-300 mb-3" />

        <div className="flex justify-between items-center mb-1">
          <span className="font-semibold text-gray-900">Total payable</span>
          <span className="font-semibold text-gray-900">
            {formatMoney(data?.payable || 0)}
          </span>
        </div>

        <div className="text-right mt-1 text-primary1 font-semibold text-lg">
          {formatMoney(data?.payable || 0)}
        </div>
      </div>
    </div>
  );
}

export default RenameConfirmationPriceBreakdown;

const StatusBadge = ({ data }) => {
  const { statusLabel, tone } = getChangeAdminGuidance(data);
  const className =
    tone === "success"
      ? "bg-green-100 text-green-700"
      : tone === "danger"
        ? "bg-red-100 text-red-700"
        : tone === "wait"
          ? "bg-[#1E61DC]/10 text-[#1E61DC]"
          : "bg-[#FE9F29]/10 text-[#FE9F29]";

  return (
    <span className={`${className} text-sm font-semibold px-3 py-1 rounded-md`}>
      {statusLabel}
    </span>
  );
};
